// CustomSelect.tsx

import React, { useState, useCallback } from 'react';
import { ChevronDown, Check } from 'lucide-react'; // Ensure you have lucide-react installed or replace with another icon library

interface Option {
    id: string;
    name: string;
}

interface CustomSelectProps {
    options: Option[];
    selected: Option | Option[];
    onChange: (selected: Option | Option[]) => void;
    placeholder: string;
    multiple?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
                                                       options,
                                                       selected,
                                                       onChange,
                                                       placeholder,
                                                       multiple = false,
                                                   }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = React.useRef<HTMLDivElement>(null);

    const handleSelect = (option: Option) => {
        if (multiple) {
            const newValues = selected.some((s: Option) => s.id === option.id)
                ? (selected as Option[]).filter((s) => s.id !== option.id)
                : [...(selected as Option[]), option];
            onChange(newValues);
        } else {
            onChange(option);
            setIsOpen(false);
        }
    };

    const handleClickOutside = useCallback((e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) {
            setIsOpen(false);
        }
    }, []);

    React.useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full px-4 py-2.5 text-left bg-white border border-gray-300 rounded-xl flex items-center justify-between hover:border-indigo-400 transition-colors"
            >
                <span className="truncate">
                    {multiple
                        ? (selected as Option[]).map(s => s.name).join(', ') || placeholder
                        : (selected as Option)?.name || placeholder}
                </span>
                <ChevronDown size={16} className={`ml-2 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute z-20 w-full mt-2 bg-white rounded-xl shadow-lg border border-gray-100 max-h-60 overflow-y-auto">
                    {options.map((option) => (
                        <div
                            key={option.id}
                            onClick={() => handleSelect(option)}
                            className={`px-4 py-2.5 cursor-pointer flex items-center justify-between ${
                                (multiple
                                    ? (selected as Option[]).some(s => s.id === option.id)
                                    : (selected as Option)?.id === option.id)
                                    ? 'bg-indigo-50 text-indigo-700'
                                    : 'hover:bg-gray-50'
                            }`}
                        >
                            <span>{option.name}</span>
                            {(multiple
                                ? (selected as Option[]).some(s => s.id === option.id)
                                : (selected as Option)?.id === option.id) && (
                                <Check size={16} className="text-indigo-600" />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CustomSelect;
